import { useHandlerCompanionEnabled } from "@/features/stepper/steptypes/companion-config";
import { useHandlerSelection } from "./api/use-handler-selection";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { useChatStore } from "@/store/chat";
import { ChatApiError } from "./api/client";
import { useChatIdentity, useChatSession } from "./api/use-session";
import { useDemoLogin } from "./hooks/use-demo-login";
import { useChatDirectory, directoryKey } from "./api/use-directory";
import { useWorkspaceRealtime } from "./hooks/use-workspace-realtime";
import { RealtimeContext } from "./api/realtime-context";
import { ConversationDirectory } from "./components/conversation-directory";
import type { DirectoryPage, Identity, Session } from "./types";
import { TooltipProvider } from "@/shared/components/ui/tooltip";
import { Conversation } from "./components/conversation";
import { WidgetFrame } from "./components/widget-frame";
import { HOVER_MUTED_TEXT } from "./components/styles";

const SIGNIN_CLASS = "flex-1 overflow-y-auto px-6 py-8";
const SIGNIN_TEXT_CLASS = "mb-5 leading-[1.7] text-muted-foreground";
const PRIMARY_CLASS =
  "my-1.5 min-h-11 w-full cursor-pointer justify-between px-3 py-1.5 motion-reduce:transition-none";

function ChatWorkspace({
  session,
  identity,
  onLogout,
  loggingOut,
}: {
  session: Session;
  identity: Identity;
  onLogout: () => void;
  loggingOut: boolean;
}) {
  const [chatOverride, setChatOverride] = useState<{
    handlerId: string | null | undefined;
    chatId: string | null;
  } | null>(null);
  const client = useQueryClient();
  const selection = useHandlerSelection(identity);
  const companionEnabled = useHandlerCompanionEnabled();
  const embeddedChatCount = useChatStore((state) => state.embeddedChatCount);
  const selectedChatId =
    chatOverride && chatOverride.handlerId === selection.data?.chatId
      ? chatOverride.chatId
      : (selection.data?.chatId ?? null);
  const setSelectedChatId = (chatId: string | null) =>
    setChatOverride({ handlerId: selection.data?.chatId, chatId });
  const directory = useChatDirectory(identity);
  const realtime = useWorkspaceRealtime(
    identity,
    selectedChatId,
    session.mode !== "ui-mock",
  );
  const pages = directory.data?.pages ?? [];
  const chats = [
    ...new Map(
      pages.flatMap((page) => page.chats).map((chat) => [chat.chatId, chat]),
    ).values(),
  ];
  const unread =
    pages[0]?.totalUnread ??
    chats.reduce((total, chat) => total + chat.unreadCount, 0);
  const selected = chats.find((chat) => chat.chatId === selectedChatId);
  const handlerChat = chats.find(
    (chat) => chat.chatId === selection.data?.chatId,
  );
  const handlerPeer = handlerChat?.participants.find(
    (person) => person.role === "ASSISTANT",
  );
  const handlerSession =
    companionEnabled && selection.data?.handler && handlerPeer
      ? {
          ...session,
          assistant: {
            memberId: handlerPeer.memberId,
            name: selection.data.handler,
          },
        }
      : session;
  const visibleChats =
    companionEnabled && selection.data?.chatId
      ? chats.filter(
          (chat) =>
            chat.kind !== "P" ||
            !chat.participants.some((person) => person.role === "ASSISTANT") ||
            chat.chatId === selection.data.chatId,
        )
      : chats;

  const resetPages = async () => {
    await client.cancelQueries({
      queryKey: directoryKey(identity),
      exact: true,
    });
    client.setQueryData<InfiniteData<DirectoryPage, string>>(
      directoryKey(identity),
      (data) =>
        data ? { pages: data.pages.slice(0, 1), pageParams: [""] } : data,
    );
    await directory.refetch();
  };
  if (embeddedChatCount > 0) return null;
  if (companionEnabled && !selection.data) {
    if (!selection.isError) return null;
    return (
      <WidgetFrame>
        <div className={SIGNIN_CLASS}>
          <p role="alert" className={SIGNIN_TEXT_CLASS}>
            Couldn’t load your handler. Please try again.
          </p>
          <Button
            className={PRIMARY_CLASS}
            disabled={selection.isFetching}
            onClick={() => void selection.refetch()}
          >
            {selection.isFetching ? "Retrying…" : "Retry handler connection"}
          </Button>
        </div>
      </WidgetFrame>
    );
  }
  if (companionEnabled && !selection.data?.handler) return null;
  return (
    <RealtimeContext.Provider value={realtime}>
      <TooltipProvider>
        {selectedChatId ? (
          <Conversation
            key={selectedChatId}
            identity={identity}
            chatId={selectedChatId}
            session={handlerSession}
            onLogout={onLogout}
            loggingOut={loggingOut}
            totalUnread={unread}
            summary={selected}
            onBack={() => setSelectedChatId(null)}
          />
        ) : (
          <ConversationDirectory
            identity={identity}
            session={handlerSession}
            chats={visibleChats}
            loading={directory.isPending}
            error={directory.error}
            onRetry={() => void directory.refetch()}
            onSelect={setSelectedChatId}
            onLogout={onLogout}
            loggingOut={loggingOut}
            totalUnread={unread}
            connection={realtime.connection}
            hasMore={directory.hasNextPage}
            loadingMore={directory.isFetchingNextPage}
            onLoadMore={() => void directory.fetchNextPage()}
            hasEarlierPages={Boolean(directory.data?.pageParams[0])}
            onResetPages={() => void resetPages()}
          />
        )}
      </TooltipProvider>
    </RealtimeContext.Provider>
  );
}

const DEMO_MODES = new Set(["local-demo", "ui-mock"]);

function EnabledWidget() {
  const companionEnabled = useHandlerCompanionEnabled();
  const session = useChatSession();
  const identity = useChatIdentity();
  const demoLogin = useDemoLogin(session.data);
  const clearPrivate = useChatStore((state) => state.clearPrivate);
  if (companionEnabled && !session.data?.identity) return null;
  // A real (non-demo) session endpoint answers 401 to visitors who are not
  // signed in: show no launcher at all rather than a demo chooser or an error.
  if (session.error instanceof ChatApiError && session.error.status === 401)
    return null;
  // Defensive twin of the 401 gate: a real session with no identity never
  // falls through to the demo chooser. Only demo modes offer test identities.
  if (
    session.data &&
    !session.data.identity &&
    !DEMO_MODES.has(session.data.mode)
  )
    return null;
  const changeIdentity = (memberId: string | null) =>
    identity.mutate(memberId, {
      onSuccess: () => clearPrivate(),
    });
  return createPortal(
    <>
      {demoLogin.isPending || demoLogin.error ? (
        <WidgetFrame>
          <div className={SIGNIN_CLASS}>
            {session.error ? (
              <>
                <p role="alert" className={SIGNIN_TEXT_CLASS}>
                  {session.error.message}
                </p>
                <Button
                  className={PRIMARY_CLASS}
                  onClick={() => void session.refetch()}
                >
                  Retry connection
                </Button>
              </>
            ) : demoLogin.error ? (
              <>
                <p role="alert" className={SIGNIN_TEXT_CLASS}>
                  {demoLogin.error.message}
                </p>
                <Button className={PRIMARY_CLASS} onClick={demoLogin.retry}>
                  Retry sign-in
                </Button>
              </>
            ) : (
              <p role="status" className={SIGNIN_TEXT_CLASS}>
                Signing in…
              </p>
            )}
          </div>
        </WidgetFrame>
      ) : session.data?.identity ? (
        <ChatWorkspace
          key={`${session.data.identity.clientId}:${session.data.identity.memberId}`}
          session={session.data}
          identity={session.data.identity}
          onLogout={() => changeIdentity(null)}
          loggingOut={identity.isPending}
        />
      ) : (
        <WidgetFrame>
          <div className={SIGNIN_CLASS}>
            <span className="font-mono text-xs tracking-[0.14em] text-gold-soft">
              LOCAL CHAT PREVIEW
            </span>
            <h3 className="my-3.5 font-serif text-[28px] leading-tight">
              Your conversations, close at hand.
            </h3>
            <p className={SIGNIN_TEXT_CLASS}>
              Choose a test identity to message contacts and create groups.
            </p>
            {session.data?.identities.map((item) => (
              <Button
                key={item.memberId}
                className={PRIMARY_CLASS}
                disabled={identity.isPending}
                onClick={() => changeIdentity(item.memberId)}
              >
                Continue as {item.name}
                <ArrowRight />
              </Button>
            ))}
            {session.isPending && (
              <p className={SIGNIN_TEXT_CLASS}>Connecting…</p>
            )}
            {session.error && (
              <>
                <p role="alert" className={SIGNIN_TEXT_CLASS}>
                  {session.error.message}
                </p>
                <Button
                  variant="ghost"
                  className={cn(
                    "min-h-11 min-w-11 cursor-pointer",
                    HOVER_MUTED_TEXT,
                  )}
                  onClick={() => void session.refetch()}
                >
                  Retry connection
                </Button>
              </>
            )}
            <small className="mt-5 block text-xs text-muted-foreground">
              Local test identities · {session.data?.model ?? "Checking model"}
            </small>
          </div>
        </WidgetFrame>
      )}
      {identity.error && (
        <div
          className="fixed right-6 bottom-26 z-[2147483001] max-w-[370px] rounded-lg bg-primary p-4 text-primary-foreground"
          role="alert"
        >
          {identity.error.message}
          <Button
            variant="link"
            className="mt-2 flex h-auto cursor-pointer p-0 text-primary-foreground underline"
            onClick={() => identity.reset()}
          >
            Dismiss
          </Button>
        </div>
      )}
    </>,
    document.body,
  );
}

/** Mounted beside the root outlet so navigation never tears down chat sync. */
export function ChatWidget() {
  const companionEnabled = useHandlerCompanionEnabled();
  return import.meta.env.VITE_WEBCHAT_ENABLED === "true" || companionEnabled ? (
    <EnabledWidget />
  ) : null;
}
